import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { IPaymentFilters } from "@/interfaces/dashboard.interface";

interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  filters: IPaymentFilters;
  onApply: (filters: IPaymentFilters) => void;
}

const statusOptions = [
  { value: "confirmado" as const, label: "Confirmado" },
  { value: "pendente" as const, label: "Pendente" },
  { value: "rejeitado" as const, label: "Rejeitado" },
  { value: "enviado" as const, label: "Enviado" },
];

export default function FilterDialog({ open, onClose, filters, onApply }: FilterDialogProps) {
  const [localFilters, setLocalFilters] = useState<IPaymentFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, open]);

  const handleStatusToggle = (status: typeof statusOptions[number]['value']) => {
    setLocalFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const handleApply = () => {
    onApply(localFilters);
  };

  const handleClear = () => {
    const clearedFilters: IPaymentFilters = {
      status: [],
      dateFrom: "",
      dateTo: "",
      valueMin: 0,
      valueMax: Infinity,
    };
    setLocalFilters(clearedFilters);
    onApply(clearedFilters);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-darkblue border-gray-600 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-semibold">
            Filtros
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Status</p>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`status-${option.value}`}
                  checked={localFilters.status.includes(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Date Range Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Período</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="dateFrom" className="text-xs text-gray-400 mb-1 block">
                  De
                </label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
              <div>
                <label htmlFor="dateTo" className="text-xs text-gray-400 mb-1 block">
                  Até
                </label>
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
            </div>
          </div>

          {/* Value Range Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Valor</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="valueMin" className="text-xs text-gray-400 mb-1 block">
                  Mínimo
                </label>
                <Input
                  id="valueMin"
                  type="number"
                  placeholder="R$ 0"
                  value={localFilters.valueMin || ""}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      valueMin: Number(e.target.value) || 0,
                    }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
              <div>
                <label htmlFor="valueMax" className="text-xs text-gray-400 mb-1 block">
                  Máximo
                </label>
                <Input
                  id="valueMax"
                  type="number"
                  placeholder="R$ ∞"
                  value={
                    localFilters.valueMax === Infinity ? "" : localFilters.valueMax || ""
                  }
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      valueMax: Number(e.target.value) || Infinity,
                    }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex-1"
          >
            Limpar
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
